import {
  Button,
  Stack,
  Box,
  Slide,
  Slider,
  styled,
  ClickAwayListener,
} from "@mui/material";
import React, { useCallback, useEffect } from "react";
import { tease } from "../components/CheckButton";
import {
  AccountType,
  ButtonStyle,
  Color,
  ContactType,
  ErrorCode,
  FontStyle,
  navPaddingTop,
  QuestionData,
  SurveyColumn,
} from "../constants";
import FavoriteIcon from "@mui/icons-material/Favorite";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import { StyledTextField } from "../components/AutocompleteUtils";
import { useDispatch, useSelector } from "react-redux";
import { stringEquals } from "../utils/utils";
import SettingsSort from "../components/SettingsSort";
import EmailSlider from "../components/EmailSlider";
import * as apiService from "../service/ApiService";
import { useNavigate } from "react-router-dom";
import { logout } from "../service/AuthService";
import FettiRadio from "../components/FettiRadio";
import ProfilePicManager from "../components/ProfilePicManager";

const styles = {
  outer: {
    paddingTop: navPaddingTop,
  },
  container: {
    // marginTop: '67px',
    paddingTop: "3rem",
    paddingBottom: "110px",
    px: 4,
    backgroundColor: "#fff",
    // backgroundColor: "red",
    height: "100%",
    display: "flex",
    alignItems: "stretch",
    minHeight: "calc(100vh - 67px)",
  },
  inner: {
    width: "100%",
    maxWidth: "24.38rem",
    mx: "auto",
    textAlign: "center",
    // backgroundColor: "green",
  },
  heading: {
    ...FontStyle.headingBody,
    fontSize: "1.73rem",
  },
  panel: {
    position: "absolute",
    width: "100%",
    backgroundColor: "#fff",
    borderRadius: "0.75rem",
    padding: "1.2rem",
    // marginBottom: "1.5rem",
    "& .MuiFormControl-root": {
      border: "none",
    },
  },
  label: {
    ...FontStyle.headingBody,
    fontSize: "1.2rem",
  },
  search: {
    width: "100%",
    maxWidth: "500px",
    backgroundColor: "rgb(255, 255, 255)",
    borderRadius: "4px",
    border: "1px solid rgb(204, 204, 204)",
  },
  button: {
    ...FontStyle.headingBody,
    width: "100%",
    fontSize: "1rem",
    textTransform: "none",
    borderRadius: "6.25rem",
    padding: "0.4rem 2rem",
    transition: "all 0.3s ease-in-out 0s",
  },
  deleteLink: {
    ...FontStyle.headingBody,
    width: "100%",
    textAlign: "center",
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  body: {
    ...FontStyle.body,
    fontSize: "1rem",
    textAlign: "left",
    lineHeight: "1.3",
  },
  radioRow: {
    ...FontStyle.body,
    fontSize: "14px",
    width: "100%",
    cursor: "pointer",
  },
  error: {
    ...FontStyle.body,
    color: "red",
    fontSize: "0.75rem",
    width: "100%",
    textAlign: "left",
  },
  success: {
    ...FontStyle.body,
    color: "green",
    fontSize: "0.75rem",
    width: "100%",
    textAlign: "left",
  },
};

const LabelInputStack = ({ label, children }) => {
  return (
    <Stack
      direction="column"
      spacing="0.5rem"
      sx={{
        width: "100%",
      }}
      alignItems="flex-start">
      <Box sx={{ ...styles.label, fontSize: "1rem" }}>{label}</Box>
      {children}
    </Stack>
  );
};

const linkedinIdx = QuestionData.indexOf(
  QuestionData.find((q) => q.column === SurveyColumn.linkedin)
);
const emailFreqIdx = QuestionData.indexOf(
  QuestionData.find((q) => q.column === SurveyColumn.email_frequency)
);
// console.log(linkedinIdx, "linkedinIdx", emailFreqIdx, "emailFreqIdx");

const PanelHeightTracker = ({ children, display, setHeight }) => {
  const handleHeight = useCallback((node) => {
    if (!node) return;
    setHeight(node.clientHeight);
    const resizeObserver = new ResizeObserver(() => {
      if (!node) return;
      setHeight(node.clientHeight);
    });
    resizeObserver.observe(node);
  }, []);
  return (
    <Slide
      direction={display ? "left" : "right"}
      in={display}
      mountOnEnter
      unmountOnExit>
      <Stack
        sx={styles.panel}
        direction="column"
        alignItems="center"
        spacing="1rem"
        ref={handleHeight}>
        {children}
      </Stack>
    </Slide>
  );
};

const SecurityPanel = ({ idx, targetIdx, setPanelHeights }) => {
  const display = idx === targetIdx;
  const auth = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [pwChanged, setPwChanged] = React.useState(false);
  const userHasPassword = auth.accountData.contactType === ContactType.email;
  const [pwSettings, setPwSettings] = React.useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [deleteStep, setDeleteStep] = React.useState(0);
  const changePassSuccess = () => {
    setPwSettings({
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    });
    setPwChanged(true);
  };
  const handleChangePass = () => {
    setPwChanged(false);
    apiService.updatePassword(
      dispatch,
      {
        ...auth.accountData,
        ...pwSettings,
        //   contactInfo, //   contactType, //   currentPassword, //   newPassword, //   confirmNewPassword,
      },
      changePassSuccess
    );
  };
  const handleDeleteAccount = () => {
    apiService.deleteAccount(dispatch, auth.accountData, () => {
      logout(navigate, dispatch);
      alert("Your account has been deleted. We're sorry to see you go!");
    });
  };
  return (
    <PanelHeightTracker
      display={display}
      setHeight={(newHeight) => {
        setPanelHeights((prev) => {
          const newHeights = [...prev];
          newHeights[targetIdx] = newHeight;
          return newHeights;
        });
      }}>
      {userHasPassword && (
        <Stack
          direction="column"
          alignItems="center"
          spacing="1rem"
          sx={{ width: "100%" }}>
          <StyledTextField
            InputProps={{ disableUnderline: true }}
            sx={styles.search}
            variant="standard"
            placeholder="Current Password"
            value={pwSettings.currentPassword}
            type="password"
            onChange={(e) =>
              setPwSettings({
                ...pwSettings,
                currentPassword: e.target.value,
              })
            }
          />
          <StyledTextField
            InputProps={{ disableUnderline: true }}
            sx={styles.search}
            variant="standard"
            placeholder="New Password"
            value={pwSettings.newPassword}
            type="password"
            onChange={(e) =>
              setPwSettings({ ...pwSettings, newPassword: e.target.value })
            }
          />
          <StyledTextField
            InputProps={{ disableUnderline: true }}
            sx={styles.search}
            variant="standard"
            placeholder="Confirm New Password"
            value={pwSettings.confirmNewPassword}
            type="password"
            onChange={(e) =>
              setPwSettings({
                ...pwSettings,
                confirmNewPassword: e.target.value,
              })
            }
          />
          {auth.error === ErrorCode.invalidCredentials && (
            <Box sx={styles.error}>Incorrect password</Box>
          )}
          {(auth.error === ErrorCode.passwordsDoNotMatch ||
            (pwSettings.newPassword &&
              pwSettings.confirmNewPassword &&
              pwSettings.newPassword !== pwSettings.confirmNewPassword)) && (
            <Box sx={styles.error}>Passwords do not match</Box>
          )}
          {pwChanged && (
            <Box sx={styles.success}>Password changed successfully!</Box>
          )}
          <Button
            sx={{
              ...styles.button,
              ...ButtonStyle.primary,
            }}
            disabled={
              !pwSettings.currentPassword ||
              !pwSettings.newPassword ||
              !pwSettings.confirmNewPassword
            }
            onClick={handleChangePass}>
            Change Password
          </Button>
          <Box sx={{ py: 1, width: "100%" }}>
            <Box
              sx={{
                width: "100%",
                margin: "0 auto",
                height: "1px",
                backgroundColor: "#0005",
              }}
            />
          </Box>
        </Stack>
      )}
      <Stack
        direction="column"
        alignItems="center"
        spacing="1rem"
        sx={{ width: "100%" }}>
        <Button
          sx={{
            ...styles.button,
            ...ButtonStyle.offWhite,
          }}
          onClick={() => {
            logout(navigate, dispatch);
          }}>
          Log out
        </Button>
        {deleteStep == 0 && (
          <Box sx={styles.deleteLink} onClick={() => setDeleteStep(1)}>
            Delete my account
          </Box>
        )}
        {deleteStep == 1 && (
          <ClickAwayListener onClickAway={() => setDeleteStep(0)}>
            <Button
              sx={{
                ...styles.button,
                ...ButtonStyle.red,
              }}
              onClick={() => {
                handleDeleteAccount();
              }}>
              Confirm delete account
            </Button>
          </ClickAwayListener>
        )}
        {/* <Box
          sx={{
            ...styles.label,
            textAlign: "left",
            width: "100%",
            paddingBottom: 0.5,
          }}>
          New match notifications
        </Box>
        <Box sx={{ ...styles.body, position: "relative", top: "-2px" }}>
          We'll notify you if any new jobs match your preferences
        </Box> */}
      </Stack>
    </PanelHeightTracker>
  );
};

const ProfilePanel = ({ idx, targetIdx, setPanelHeights }) => {
  const display = idx === targetIdx;
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [settings, setSettings] = React.useState({
    firstName: "",
    lastName: "",
    // email: "",
    linkedin: "",
    emailFreq: 0,
    accountType: "",
  });
  const [unsavedChanges, setUnsavedChanges] = React.useState(false);
  const accountData = useSelector((state) => state.auth.accountData);
  const linkedinUrl = useSelector(
    (state) => state.survey.selections[linkedinIdx]
  )[0];
  const emailFreq = useSelector(
    (state) => state.survey.selections[emailFreqIdx]
  )[0];
  const saveSettings = () => {
    apiService.updateSettings(dispatch, {
      ...auth.accountData,
      settings: {
        ...settings,
        emailFreq: QuestionData[emailFreqIdx].options[2 - settings.emailFreq],
      },
    });
  };
  // when server updates settings, update local settings
  useEffect(() => {
    if (!accountData) return;
    console.log("newbasic info", "~" + linkedinUrl + "~", emailFreq);
    setSettings({
      ...settings,
      firstName: accountData.firstName || "",
      lastName: accountData.lastName || "",
      //   email: accountData.contactInfo || "",
      linkedin: linkedinUrl || "",
      emailFreq: 2 - emailFreq,
      accountType: accountData.accountType,
    });
  }, [accountData, linkedinUrl, emailFreq]);
  // if local settings are different from server settings, show unsaved changes
  useEffect(() => {
    if (!accountData) return;
    console.log(
      "unsave",
      stringEquals(settings.firstName, accountData.firstName),
      stringEquals(settings.lastName, accountData.lastName),
      //   !stringEquals(settings.email, accountData.contactInfo) ||
      stringEquals(settings.linkedin, linkedinUrl),
      settings.emailFreq !== 2 - emailFreq,
      settings.emailFreq,
      2 - emailFreq,
      settings.accountType !== accountData.accountType,
      `~${settings.accountType}~`,
      `~${accountData.accountType}~`
    );
    if (
      !stringEquals(settings.firstName, accountData.firstName) ||
      !stringEquals(settings.lastName, accountData.lastName) ||
      //   !stringEquals(settings.email, accountData.contactInfo) ||
      !stringEquals(settings.linkedin, linkedinUrl) ||
      settings.emailFreq !== 2 - emailFreq ||
      settings.accountType !== accountData.accountType
    ) {
      setUnsavedChanges(true);
    } else {
      setUnsavedChanges(false);
    }
  }, [settings, accountData]);
  return (
    <PanelHeightTracker
      display={display}
      setHeight={(newHeight) => {
        setPanelHeights((prev) => {
          const newHeights = [...prev];
          newHeights[targetIdx] = newHeight;
          return newHeights;
        });
      }}>
      <Box sx={styles.label}>Settings</Box>
      <ProfilePicManager />
      <LabelInputStack label="Email">
        <StyledTextField
          InputProps={{ disableUnderline: true }}
          sx={styles.search}
          variant="standard"
          placeholder="Email"
          // value={settings.email}
          value={accountData.contactInfo || ""}
          disabled
          // onChange={(e) =>
          //   setSettings({ ...settings, email: e.target.value })
          // }
        />
      </LabelInputStack>
      <LabelInputStack label="First name">
        <StyledTextField
          InputProps={{ disableUnderline: true }}
          sx={styles.search}
          variant="standard"
          placeholder="First name"
          value={settings.firstName}
          onChange={(e) =>
            setSettings({ ...settings, firstName: e.target.value })
          }
        />
      </LabelInputStack>
      <LabelInputStack label="Last name">
        <StyledTextField
          InputProps={{ disableUnderline: true }}
          sx={styles.search}
          variant="standard"
          placeholder="Last name"
          value={settings.lastName}
          onChange={(e) =>
            setSettings({ ...settings, lastName: e.target.value })
          }
        />
      </LabelInputStack>
      <LabelInputStack label="LinkedIn URL">
        <StyledTextField
          InputProps={{ disableUnderline: true }}
          sx={styles.search}
          variant="standard"
          placeholder="https://linkedin.com/in/your-name"
          value={settings.linkedin}
          onChange={(e) =>
            setSettings({ ...settings, linkedin: e.target.value })
          }
        />
      </LabelInputStack>
      <Box
        sx={{
          ...styles.label,
          fontSize: "1rem",
          width: "100%",
          textAlign: "left",
        }}>
        Email Notifications
      </Box>
      <EmailSlider
        defaultValue={emailFreq}
        value={settings.emailFreq}
        setValue={(v) => setSettings({ ...settings, emailFreq: v })}
      />
      <Box
        sx={{
          ...styles.label,
          fontSize: "1rem",
          width: "100%",
          textAlign: "left",
        }}>
        I am interested in seeing:
      </Box>
      <Stack
        direction="row"
        sx={styles.radioRow}
        spacing={1}
        alignItems="center"
        onClick={() =>
          setSettings({ ...settings, accountType: AccountType.standard })
        }>
        <FettiRadio
          selected={settings.accountType === AccountType.standard}
          size="16px"
        />
        <Box>Full-time / Part-time Work</Box>
      </Stack>
      <Stack
        direction="row"
        sx={styles.radioRow}
        spacing={1}
        alignItems="center"
        onClick={() =>
          setSettings({ ...settings, accountType: AccountType.gig })
        }>
        <FettiRadio
          selected={settings.accountType === AccountType.gig}
          size="16px"
        />
        <Box>Gig Work</Box>
      </Stack>
      {unsavedChanges && (
        <Button
          sx={{
            ...styles.button,
            ...ButtonStyle.primary,
          }}
          onClick={saveSettings}>
          Save changes
        </Button>
      )}
    </PanelHeightTracker>
  );
};

const AccountSettings = () => {
  const [panelIdx, setPanelIdx] = React.useState(0);
  const [panelHeights, setPanelHeights] = React.useState([0, 0]);
  const accountData = useSelector((state) => state.auth.accountData);

  return (
    <Box sx={styles.outer}>
      <Box
        sx={{
          ...styles.container,
          minHeight: `max(calc(100vh - 67px), ${
            panelHeights[panelIdx] + 250
          }px)`,
        }}>
        <Stack
          sx={styles.inner}
          direction="column"
          alignItems="center"
          spacing="1rem">
          <Box sx={styles.heading}>Your account</Box>
          <SettingsSort idx={panelIdx} setIdx={setPanelIdx} />
          <Box
            sx={{
              position: "relative",
              width: "100%",
              // backgroundColor: "red",
              height: "100%",
            }}>
            <ProfilePanel
              idx={panelIdx}
              targetIdx={0}
              setPanelHeights={setPanelHeights}
            />
            <SecurityPanel
              idx={panelIdx}
              targetIdx={1}
              setPanelHeights={setPanelHeights}
            />
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default AccountSettings;
